import React, { useEffect, useState, useCallback } from 'react'
import { X, Copy, ChevronLeft, ChevronRight } from 'react-feather'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import Modal from '../../components/Modal'
import { RowBetween } from '../../components/Row'
import { Text } from 'rebass'
import { FillInfo, formatUsdtAmount, formatMudAmount } from '../../hooks/useOrderBook'
import { USDT_DECIMALS, MUD_DECIMALS } from '../../constants/orderbook'
import { ORDERBOOK_API_BASE } from '../../constants/orderbook'
import { InfoRow, InfoLabel, InfoValue } from './styleds'
import { useActiveWeb3React } from '../../hooks'
import { getEtherscanLink, getExplorerBlockLink, shortenTxHash } from '../../utils'
import { ExternalLink } from '../../theme/components'
import { ChainId } from '@uniswap/sdk'
import Popover from '../../components/Popover'

const ModalContent = styled.div`
  width: 100%;
  padding: 0;
`

const Header = styled(RowBetween)`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};
`

const CloseIcon = styled(X)`
  cursor: pointer;
  color: ${({ theme }) => theme.text2};
  :hover {
    color: ${({ theme }) => theme.text1};
  }
`

const List = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  padding: 0.5rem 1rem 1rem;

  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.bg1};
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.bg3};
    border-radius: 4px;
  }
`

const FillCard = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const EmptyFills = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${({ theme }) => theme.text3};
  font-size: 13px;
`

const LoadingFills = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${({ theme }) => theme.text2};
  font-size: 13px;
`

const TxHashRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
`

const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.text2};
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.bg3};
    color: ${({ theme }) => theme.text1};
  }
`

const CopyPopoverContent = styled.div`
  padding: 6px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.text1};
  white-space: nowrap;
`

const ExplorerLink = styled(ExternalLink)`
  font-size: 12px;
  font-weight: 500;
`

const PaginationWrap = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 6px;
  overflow: hidden;
  background: ${({ theme }) => theme.bg1};
`

const PaginationBtn = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-right: 1px solid ${({ theme }) => theme.bg3};
  background: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text1};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:last-of-type {
    border-right: none;
    border-left: 1px solid ${({ theme }) => theme.bg3};
  }
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.bg2};
  }
`

const PageInfo = styled.span`
  min-width: 64px;
  padding: 0 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.text1};
  text-align: center;
  line-height: 32px;
`

interface OrderFillsModalProps {
  isOpen: boolean
  onDismiss: () => void
  orderId: number | null
  /** true = buy (fee in USDT), false = sell (fee in MUD) */
  isBuy?: boolean
}

const FILLS_PAGE_SIZE = 20

export default function OrderFillsModal({ isOpen, onDismiss, orderId, isBuy = true }: OrderFillsModalProps) {
  const { t } = useTranslation()
  const { chainId } = useActiveWeb3React()
  const [fills, setFills] = useState<FillInfo[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copiedFillId, setCopiedFillId] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen || orderId == null) {
      setFills([])
      setTotal(0)
      setPage(1)
      setError(null)
      return
    }
    setPage(1)
  }, [isOpen, orderId])

  useEffect(() => {
    if (!isOpen || orderId == null) return
    let cancelled = false
    setLoading(true)
    setError(null)
    const url = `${ORDERBOOK_API_BASE}/orders/${orderId}/fills?page=${page}&pageSize=${FILLS_PAGE_SIZE}`
    fetch(url)
      .then(res => res.json())
      .then((data: { code?: number; data?: { items?: FillInfo[]; total?: number } }) => {
        if (cancelled) return
        const items = data?.data?.items
        const totalCount = data?.data?.total ?? (Array.isArray(items) ? items.length : 0)
        setFills(Array.isArray(items) ? items : [])
        setTotal(totalCount)
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load')
          setFills([])
          setTotal(0)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, orderId, page])

  const totalPages = total > 0 ? Math.ceil(total / FILLS_PAGE_SIZE) : 0
  const handlePageChange = useCallback(
    (nextPage: number) => {
      setPage(Math.max(1, Math.min(nextPage, totalPages || 1)))
    },
    [totalPages]
  )

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      <ModalContent>
        <Header>
          <Text fontWeight={500} fontSize={16}>
            {isBuy ? t('orderFillsTitleBuy') : t('orderFillsTitleSell')}
            {total > 0 && ` (${total})`}
          </Text>
          <CloseIcon onClick={onDismiss} />
        </Header>
        <List>
          {loading && <LoadingFills>{t('orderLoading')}</LoadingFills>}
          {error && <EmptyFills>{error}</EmptyFills>}
          {!loading && !error && fills.length === 0 && <EmptyFills>{t('orderNoFills')}</EmptyFills>}
          {!loading && fills.length > 0 &&
            fills.map((fill, i) => (
              <FillCard key={fill.fillId ?? i}>
                <InfoRow>
                  <InfoLabel>{t('orderFillAmount')}</InfoLabel>
                  <InfoValue>
                    {formatUsdtAmount(fill.usdtAmount)} USDT / {formatMudAmount(fill.mudAmount)} MUD
                  </InfoValue>
                </InfoRow>
                {(() => {
                  const usdtNum = parseFloat(fill.usdtAmount) / Math.pow(10, USDT_DECIMALS)
                  const mudNum = parseFloat(fill.mudAmount) / Math.pow(10, MUD_DECIMALS)
                  const fillPrice = mudNum > 0 ? usdtNum / mudNum : 0
                  return (
                    <InfoRow>
                      <InfoLabel>{t('orderFillPrice')}</InfoLabel>
                      <InfoValue>{fillPrice > 0 ? fillPrice.toFixed(4) + ' USDT/MUD' : '--'}</InfoValue>
                    </InfoRow>
                  )
                })()}
                {!isBuy && fill.fee && (
                  <InfoRow>
                    <InfoLabel>{t('orderFee')}</InfoLabel>
                    <InfoValue>{formatMudAmount(fill.fee) + ' MUD'}</InfoValue>
                  </InfoRow>
                )}
                {fill.txHash && chainId != null && (
                  <InfoRow>
                    <InfoLabel>{t('orderFillTxHash')}</InfoLabel>
                    <InfoValue>
                      <TxHashRow>
                        <ExplorerLink href={getEtherscanLink(chainId as ChainId, fill.txHash, 'transaction')} target="_blank" rel="noopener noreferrer">
                          {shortenTxHash(fill.txHash)}
                        </ExplorerLink>
                        <Popover
                          show={copiedFillId === (fill.fillId ?? i)}
                          content={<CopyPopoverContent>{t('orderCopySuccess')}</CopyPopoverContent>}
                          placement="top"
                        >
                          <CopyBtn
                            type="button"
                            onClick={() => {
                              navigator.clipboard?.writeText(fill.txHash).then(() => {
                                setCopiedFillId(fill.fillId ?? i)
                                setTimeout(() => setCopiedFillId(null), 1500)
                              })
                            }}
                            title={t('orderCopyTxHash')}
                          >
                            <Copy size={14} />
                          </CopyBtn>
                        </Popover>
                      </TxHashRow>
                    </InfoValue>
                  </InfoRow>
                )}
                {fill.blockNumber != null && chainId != null && (
                  <InfoRow>
                    <InfoLabel>{t('orderFillBlock')}</InfoLabel>
                    <InfoValue>
                      <ExplorerLink href={getExplorerBlockLink(chainId as ChainId, Number(fill.blockNumber))} target="_blank" rel="noopener noreferrer">
                        {fill.blockNumber}
                      </ExplorerLink>
                    </InfoValue>
                  </InfoRow>
                )}
                <InfoRow>
                  <InfoLabel>{t('orderFillTime')}</InfoLabel>
                  <InfoValue>{new Date(fill.timestamp).toLocaleString()}</InfoValue>
                </InfoRow>
              </FillCard>
            ))}
          {!loading && !error && totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '0.5rem' }}>
              <PaginationWrap>
                <PaginationBtn
                  type="button"
                  disabled={loading || page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('orderPrevPage')}
                >
                  <ChevronLeft size={18} />
                </PaginationBtn>
                <PageInfo>
                  {t('orderPageInfo', { current: page, total: totalPages })}
                </PageInfo>
                <PaginationBtn
                  type="button"
                  disabled={loading || page >= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('orderNextPage')}
                >
                  <ChevronRight size={18} />
                </PaginationBtn>
              </PaginationWrap>
            </div>
          )}
        </List>
      </ModalContent>
    </Modal>
  )
}
